import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { formatCount, formatTypicalNotes } from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';

import { useAppSettings } from '@/hooks/use-app-settings';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayPanel } from '@/components/full-screen-overlay';

import { formatPriceWithCents } from '../billing-format';
import { clearRechargeCheckout, isMarkedRechargeReturn } from '../recharge-return';

import styles from './recharge-success.module.scss';

/** Mounted on Subscription & Billing; Checkout's success URL opens it with `?recharged=<joules>-<amountCents>`. */
export const RechargeSuccess = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.rechargeSuccess, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <RechargeSuccessGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/**
 * A value that is not `<joules>-<amountCents>`, or not the one the Recharge review marked before leaving for
 * Stripe, closes the overlay unseen; showing clears the mark, so Back, a reload or a shared link shows nothing.
 * The value is read once on mount, so the content stays through the fade-out after closing removes the param.
 */
const RechargeSuccessGate = ({ onClose }: { onClose: () => void }) => {
  const { search } = useLocation();
  const [match] = useState(() => {
    const value = new URLSearchParams(search).get(urlOverlays.rechargeSuccess.param) ?? '';
    return isMarkedRechargeReturn(value) ? /^(\d+)-(\d+)$/.exec(value) : null;
  });

  useEffect(() => {
    if (match) clearRechargeCheckout();
    else onClose();
  }, [match, onClose]);

  return match && <RechargeSuccessDialog joules={Number(match[1])} amountCents={Number(match[2])} onClose={onClose} />;
};

type RechargeSuccessDialogProps = { joules: number; amountCents: number; onClose: () => void };

/**
 * Shown at once on the return, without waiting for the balance: Stripe redirects only after payment, and the
 * Energy card under the overlay updates live when the webhook credits the balance.
 */
const RechargeSuccessDialog = ({ joules, amountCents, onClose }: RechargeSuccessDialogProps) => {
  const typicalNotes = formatTypicalNotes(joules, useAppSettings()?.creditsPerTypicalNote);

  return (
    <FullScreenOverlay
      title={`${formatCount(joules)} Joules added`}
      subtitle="Your lasting reserve is ready. Monthly Energy is used first."
      onEscape={onClose}
    >
      <OverlayPanel title="Payment confirmed">
        <div>{`${formatCount(joules)} Joules · ${formatPriceWithCents(amountCents)} USD`}</div>
        {typicalNotes && <div>{typicalNotes}</div>}
        <div className={styles.reserveNote}>
          Added to your Joule balance. Unused Joules do not expire while your account exists.
        </div>
      </OverlayPanel>
      {/* Closing reveals Subscription & Billing, whose Energy card already shows the live balance. */}
      <Button appearance="clear" size="big" fullWidth cornerRadius="smooth" onClick={onClose}>
        View Energy
      </Button>
    </FullScreenOverlay>
  );
};
